import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Globe,
  Instagram,
  Shield,
  ShieldOff,
  UserX,
  UserCheck,
  Trash2,
  Save,
  ExternalLink,
  TrendingUp,
  FileText,
  Eye,
  Send,
  AlertCircle,
} from 'lucide-react';
import {
  getAdminUserDetail,
  toggleUserSuspension,
  updateUserRole,
  updateAdminNotes,
  deleteUser,
} from '../../services/adminService';
import type { AdminUserDetail } from '../../types/admin';
import { logger } from '../../lib/logger';

export function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminUserDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [adminNotes, setAdminNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');

  useEffect(() => {
    if (id) {
      loadUserDetail();
    }
  }, [id]);

  const loadUserDetail = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const result = await getAdminUserDetail(id);
      setData(result);
      setAdminNotes(result.profile?.admin_notes || '');
    } catch (error) {
      logger.error('Error loading user detail:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleSuspension = async () => {
    if (!data?.profile) return;
    setActionLoading('suspension');
    try {
      await toggleUserSuspension(data.profile.id, !data.profile.is_suspended);
      setData((prev) =>
        prev
          ? {
              ...prev,
              profile: { ...prev.profile, is_suspended: !prev.profile.is_suspended },
            }
          : null
      );
    } catch (error) {
      logger.error('Error toggling suspension:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleRole = async () => {
    if (!data?.profile) return;
    setActionLoading('role');
    try {
      const newRole = data.profile.role === 'admin' ? 'user' : 'admin';
      await updateUserRole(data.profile.id, newRole);
      setData((prev) =>
        prev ? { ...prev, profile: { ...prev.profile, role: newRole } } : null
      );
    } catch (error) {
      logger.error('Error updating role:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveNotes = async () => {
    if (!data?.profile) return;
    setIsSavingNotes(true);
    try {
      await updateAdminNotes(data.profile.id, adminNotes);
    } catch (error) {
      logger.error('Error saving notes:', error);
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleDelete = async () => {
    if (!data?.profile || confirmEmail !== data.profile.email) return;
    setActionLoading('delete');
    try {
      await deleteUser(data.profile.id);
      navigate('/admin/users');
    } catch (error) {
      logger.error('Error deleting user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data?.profile) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
        <p className="text-slate-400">Nutzer nicht gefunden</p>
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-2 mt-4 text-orange-400 hover:text-orange-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  const { profile, site_pages, lead_stats, recent_page_events, recent_submissions } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/users"
            className="p-2 bg-slate-800 text-slate-400 rounded-xl hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-4">
            {profile.profile_image_url ? (
              <img
                src={profile.profile_image_url}
                alt=""
                className="w-16 h-16 rounded-2xl object-cover"
              />
            ) : (
              <div className="w-16 h-16 bg-slate-700 rounded-2xl flex items-center justify-center">
                <User className="w-8 h-8 text-slate-400" />
              </div>
            )}
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-white">
                  {profile.full_name || 'Kein Name'}
                </h1>
                {profile.role === 'admin' && (
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm">
                    <Shield className="w-3.5 h-3.5" />
                    Admin
                  </span>
                )}
                <span
                  className={`px-2.5 py-1 rounded-full text-sm ${
                    profile.is_suspended
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-emerald-500/20 text-emerald-400'
                  }`}
                >
                  {profile.is_suspended ? 'Gesperrt' : 'Aktiv'}
                </span>
              </div>
              <p className="text-slate-400 mt-1">{profile.email}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleToggleSuspension}
            disabled={actionLoading === 'suspension'}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors disabled:opacity-50 ${
              profile.is_suspended
                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                : 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
            }`}
          >
            {profile.is_suspended ? (
              <>
                <UserCheck className="w-4 h-4" />
                Entsperren
              </>
            ) : (
              <>
                <UserX className="w-4 h-4" />
                Sperren
              </>
            )}
          </button>
          <button
            onClick={handleToggleRole}
            disabled={actionLoading === 'role'}
            className="flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-400 rounded-xl hover:bg-violet-500/30 transition-colors disabled:opacity-50"
          >
            {profile.role === 'admin' ? (
              <>
                <ShieldOff className="w-4 h-4" />
                Admin entfernen
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                Zum Admin machen
              </>
            )}
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Löschen
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Info */}
        <div className="space-y-6">
          {/* Contact Info */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Kontaktinformationen</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-500" />
                <span className="text-slate-300">{profile.email}</span>
              </div>
              {profile.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-300">{profile.phone}</span>
                </div>
              )}
              {(profile.city || profile.country) && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-slate-500" />
                  <span className="text-slate-300">
                    {[profile.city, profile.country].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>

            {/* Social Links */}
            {(profile.instagram_url || profile.facebook_url || profile.tiktok_url || profile.linkedin_url) && (
              <div className="mt-6 pt-6 border-t border-slate-800">
                <h3 className="text-sm font-medium text-slate-400 mb-3">Social Media</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.instagram_url && (
                    <a
                      href={profile.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                    >
                      <Instagram className="w-4 h-4" />
                      Instagram
                    </a>
                  )}
                  {profile.facebook_url && (
                    <a
                      href={profile.facebook_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors text-sm"
                    >
                      <Globe className="w-4 h-4" />
                      Facebook
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Account-Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-400">Registriert</span>
                <span className="text-slate-300">{formatDate(profile.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Zielgruppe</span>
                <span className="text-slate-300">{profile.target_audience || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Produktfokus</span>
                <span className="text-slate-300">{profile.product_focus || '-'}</span>
              </div>
            </div>
          </div>

          {/* Admin Notes */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Admin-Notizen</h2>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Notizen zu diesem Nutzer..."
              className="w-full h-32 px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <button
              onClick={handleSaveNotes}
              disabled={isSavingNotes}
              className="mt-3 flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSavingNotes ? 'Speichert...' : 'Speichern'}
            </button>
          </div>
        </div>

        {/* Middle Column - Stats & Pages */}
        <div className="space-y-6">
          {/* Lead Stats */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-5 h-5 text-cyan-400" />
              <h2 className="text-lg font-semibold text-white">CRM-Statistiken</h2>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-800 rounded-xl">
                <p className="text-2xl font-bold text-white">{lead_stats.total}</p>
                <p className="text-xs text-slate-400 mt-1">Gesamt Leads</p>
              </div>
              <div className="text-center p-4 bg-slate-800 rounded-xl">
                <p className="text-2xl font-bold text-emerald-400">{lead_stats.won}</p>
                <p className="text-xs text-slate-400 mt-1">Gewonnen</p>
              </div>
              <div className="text-center p-4 bg-slate-800 rounded-xl">
                <p className="text-2xl font-bold text-white">{lead_stats.conversion_rate}%</p>
                <p className="text-xs text-slate-400 mt-1">Conversion</p>
              </div>
            </div>
            {data.last_lead_activity && (
              <p className="text-sm text-slate-400 mt-4">
                Letzte Lead-Aktivität: {formatDateTime(data.last_lead_activity)}
              </p>
            )}
          </div>

          {/* Site Pages */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="w-5 h-5 text-orange-400" />
              <h2 className="text-lg font-semibold text-white">Website-Seiten</h2>
            </div>
            {site_pages.length > 0 ? (
              <div className="space-y-3">
                {site_pages.map((page) => (
                  <div
                    key={page.id}
                    className="flex items-center justify-between p-4 bg-slate-800 rounded-xl"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{page.type}</span>
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            page.is_published
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-slate-700 text-slate-400'
                          }`}
                        >
                          {page.is_published ? 'Veröffentlicht' : 'Entwurf'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400 mt-1">
                        {page.blocks_count} Blöcke • {formatDate(page.updated_at)}
                      </p>
                    </div>
                    {page.is_published && (
                      <a
                        href={`/u/${page.slug}/${page.type.toLowerCase()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-slate-400 hover:text-white transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">Keine Seiten erstellt</p>
            )}
          </div>
        </div>

        {/* Right Column - Activity */}
        <div className="space-y-6">
          {/* Recent Page Events */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-5 h-5 text-violet-400" />
              <h2 className="text-lg font-semibold text-white">Letzte Seitenaufrufe</h2>
            </div>
            {recent_page_events.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recent_page_events.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-3 bg-slate-800 rounded-lg text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-slate-300">{event.page_type}</span>
                      <span className="text-slate-500">•</span>
                      <span className="text-slate-400">{event.event_type}</span>
                    </div>
                    <span className="text-slate-500 text-xs">
                      {formatDateTime(event.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">Keine Aktivität</p>
            )}
          </div>

          {/* Recent Submissions */}
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Send className="w-5 h-5 text-emerald-400" />
              <h2 className="text-lg font-semibold text-white">Letzte Formular-Einsendungen</h2>
            </div>
            {recent_submissions.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {recent_submissions.map((sub) => (
                  <div
                    key={sub.id}
                    className="flex items-center justify-between p-3 bg-slate-800 rounded-lg text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-500" />
                      <span className="text-slate-300">{sub.form_name}</span>
                    </div>
                    <span className="text-slate-500 text-xs">
                      {formatDateTime(sub.submitted_at)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-4">Keine Einsendungen</p>
            )}
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Nutzer endgültig löschen</h3>
            </div>

            <p className="text-slate-400 mb-4">
              Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten werden gelöscht.
            </p>

            <div className="bg-slate-800 rounded-xl p-4 mb-4">
              <p className="text-sm text-slate-400 mb-2">
                Gib zur Bestätigung die E-Mail-Adresse ein:
              </p>
              <p className="text-sm font-mono text-orange-400 mb-3">{profile.email}</p>
              <input
                type="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="E-Mail eingeben"
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setConfirmEmail('');
                }}
                disabled={actionLoading === 'delete'}
                className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={confirmEmail !== profile.email || actionLoading === 'delete'}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading === 'delete' ? 'Lösche...' : 'Endgültig löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
